import {useContext} from 'react';
import {Controller} from 'react-hook-form';
import {Link, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {Form} from '../../ui/forms/form';
import {LoginPayload, useLogin} from '../requests/use-login';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '../../i18n/trans';
import {StaticPageTitle} from '../../seo/static-page-title';
import {
  SiteConfigContext,
  SiteConfigContextValue,
} from '../../core/settings/site-config-context';
import {useSettings} from '../../core/settings/use-settings';
import {
  AuthCard,
  AuthCardWrap,
  AuthCheckbox,
  AuthCloseButton,
  AuthPromptCard,
  AuthShell,
  AuthTitle,
  authFieldClass,
  authPrimaryBtnClass,
} from './auth-ui';
import {resolveSiteLogoSrc} from '@common/branding/site-logo';
import {useIsDarkMode} from '@common/ui/themes/use-is-dark-mode';

interface Props {
  onTwoFactorChallenge: () => void;
}

export function LoginPage({onTwoFactorChallenge}: Props) {
  const [searchParams] = useSearchParams();
  const searchParamsEmail = searchParams.get('email') || undefined;
  const settings = useSettings();
  const branding = settings?.branding;
  const registrationDisabled = settings?.registration?.disable ?? false;
  const site = settings?.site ?? ({demo: false} as {demo?: boolean});
  const siteConfig = useContext(SiteConfigContext);
  const isDarkMode = useIsDarkMode();

  const demoDefaults =
    site.demo && !searchParamsEmail ? getDemoFormDefaults(siteConfig) : {};

  const form = useForm<LoginPayload>({
    defaultValues: {remember: true, email: searchParamsEmail, ...demoDefaults},
  });

  const login = useLogin(form);
  const logoSrc = resolveSiteLogoSrc(branding, isDarkMode);
  const emailValue = form.watch('email');
  const forgotPasswordTo = emailValue
    ? `/forgot-password?email=${encodeURIComponent(emailValue)}`
    : '/forgot-password';

  return (
    <AuthLayout>
      <StaticPageTitle>
        <Trans message="Login" />
      </StaticPageTitle>
      <AuthShell logoSrc={logoSrc}>
        <AuthCardWrap compact action={<AuthCloseButton />}>
          <AuthCard compact>
            <AuthTitle>
              <Trans message="Login to your account" />
            </AuthTitle>

            <Form
              form={form}
              onSubmit={payload => {
                login.mutate(payload, {
                  onSuccess: response => {
                    if (response.two_factor) {
                      onTwoFactorChallenge();
                    }
                  },
                });
              }}
            >
              <div className="flex w-full flex-col items-center gap-[16px]">
                <FormTextField
                  name="email"
                  type="email"
                  label={null}
                  placeholder="Email"
                  disabled={!!searchParamsEmail}
                  className="w-[350px] max-w-full"
                  inputClassName={authFieldClass}
                  required
                />
                <FormTextField
                  name="password"
                  type="password"
                  label={null}
                  placeholder="Password"
                  className="w-[350px] max-w-full"
                  inputClassName={authFieldClass}
                  required
                />

                <Controller
                  name="remember"
                  control={form.control}
                  render={({field}) => (
                    <AuthCheckbox
                      id="remember"
                      align="center"
                      checked={!!field.value}
                      onChange={field.onChange}
                    >
                      <Trans message="Remember me on this device" />
                    </AuthCheckbox>
                  )}
                />

                <Link
                  to={forgotPasswordTo}
                  className="text-[16px] font-normal text-[#4C4C4C] no-underline hover:text-[#4C4C4C] dark:text-[#b3bcc8] dark:hover:text-[#f3f4f6]"
                >
                  <Trans message="Reset password" />
                </Link>

                <button
                  type="submit"
                  className={authPrimaryBtnClass}
                  aria-label="Log in"
                >
                  <Trans message="Log in" />
                </button>
              </div>
            </Form>
          </AuthCard>
        </AuthCardWrap>

        {!registrationDisabled && (
          <AuthPromptCard
            text={<Trans message="Don't have a CloudTransfer account?" />}
            buttonLabel="Create"
            buttonTo="/register"
          />
        )}
      </AuthShell>
    </AuthLayout>
  );
}

function getDemoFormDefaults(siteConfig: SiteConfigContextValue) {
  if (siteConfig.demo.loginPageDefaults === 'randomAccount') {
    const number = Math.floor(Math.random() * 100) + 1;
    const paddedNumber = String(number).padStart(3, '0');
    return {
      email: `admin@demo${paddedNumber}.com`,
      password: 'admin',
    };
  }
  return {
    email: 'admin@admin.com',
    password: 'admin',
  };
}
